import { type DependencyList, type RefObject, useLayoutEffect } from 'react';

import { animationManager } from '@systems/AnimationManager';

/* `gsap.ContextFunc` and `gsap.Context` resolve from GSAP's ambient global
   namespace declaration — no value import is needed for the types alone. */

/**
 * Runs GSAP setup inside a scoped context that reverts on unmount.
 *
 * `useLayoutEffect`, not `useEffect`: animations that set an element's
 * starting state must run before the browser paints, or the element shows
 * for one frame at its untweened position and flickers.
 *
 * Scoping to a ref means selector strings inside `setup` resolve only
 * within that subtree, so one component cannot accidentally animate
 * another's elements by reusing a selector.
 *
 * `deps` controls re-runs, exactly as it would for useEffect. With the
 * default empty array the animations are built once and `setup` closes over
 * its first render's values — the same contract as @gsap/react's useGSAP.
 * Anything that should rebuild the animations must be listed in `deps`.
 */
export const useGsapContext = (
  setup: gsap.ContextFunc,
  scope?: RefObject<Element | null>,
  deps: DependencyList = [],
): void => {
  useLayoutEffect(() => {
    const context = animationManager.createContext(setup, scope?.current ?? undefined);

    return () => {
      animationManager.revertContext(context);
    };
    /* The caller owns the dependency list; `setup` is deliberately excluded
       because callers pass an inline arrow that would be a new reference on
       every render, rebuilding every animation each time. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
